/* eslint-disable react/jsx-no-undef */
import { useState, useRef, ChangeEvent, useEffect } from 'react'
import { IFleetTreeData } from '~/models'
import FleetNavItems from './FleetNavItems'
import styles from './FleetNav.module.scss'
import classNames from 'classnames/bind'
import SelectedFleet from './SelectedFleet'
import TextField from '@mui/material/TextField'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import { currentTheme, setFleet } from '~/redux/features'
import { useAppSelector } from '~/app/hooks'
import { useClickOutside } from '~/hooks'
import { useDispatch } from 'react-redux'
import { FleetFilterSelectorModel } from './FleetButton.model'
import { SearchIcon } from '../EFIcons'

const cx = classNames.bind(styles)

export const FleetFilterSelector = ({
  rootFleet,
  disabled
}: FleetFilterSelectorModel) => {
  const dispatch = useDispatch()

  const theme = useAppSelector(currentTheme)
  const detailsRef = useRef(null)
  const [selectedFleet, setSelectedFleet] = useState<IFleetTreeData | null>(rootFleet)
  const [fleetSearchString, setFleetSearchString] = useState<string | undefined>('')

  const fleetTreeFiltered = searchFleetTreeName(fleetSearchString) ?? rootFleet

  useClickOutside(detailsRef, () => closeDetailsElement())

  function findFleet(fleet: IFleetTreeData, fleetId: string): IFleetTreeData | null {
    if (fleet.id === fleetId) {
      return fleet
    }
    if (fleet.childrenFleet) {
      for (const child of fleet.childrenFleet) {
        const foundChild = findFleet(child, fleetId)
        if (foundChild) {
          return foundChild
        }
      }
    }
    return null
  }

  function searchFleetTreeName(searchString: string | undefined): IFleetTreeData | null {
    if (rootFleet && searchString) {
      const treeCopy = structuredClone(rootFleet)
      treeCopy.childrenFleet = treeCopy.childrenFleet.filter(x =>
        fleetOrChildrenNameMatches(searchString, x)
      )
      return treeCopy
    }
    return rootFleet
  }

  function fleetOrChildrenNameMatches(
    searchString: string,
    fleet: IFleetTreeData
  ): boolean {
    let localMatch: boolean = false
    for (const fragment of fleet.name.toLocaleUpperCase().split(' ')) {
      if (
        fragment.indexOf(searchString.toLocaleUpperCase()) >= 0 ||
        fragment.localeCompare(searchString, navigator.language, {
          sensitivity: 'base'
        }) === 0
      ) {
        localMatch = true
        break
      }
    }

    let childMatch: boolean = false
    if (fleet.childrenFleet && Array.isArray(fleet.childrenFleet) && fleet.childrenFleet.length > 0) {
      fleet.childrenFleet = fleet.childrenFleet.filter(x =>
        fleetOrChildrenNameMatches(searchString, x)
      )
      childMatch = fleet.childrenFleet.length > 0
    }
    return localMatch || childMatch
  }

  function closeDetailsElement() {
    if (detailsRef) {
      const detailsEle: HTMLDetailsElement =
        (detailsRef?.current as unknown as HTMLDetailsElement) ?? null
      if (detailsEle != null) {
        if (detailsEle.attributes?.getNamedItem('open')) {
          detailsEle.attributes?.removeNamedItem('open')
        }
      }
    }
  }

  function handleFleetRadioValueChange(event: ChangeEvent) {
    const input = event.target as HTMLInputElement
    if (input?.value && rootFleet) {
      const fleet = findFleet(rootFleet, input.value.trim())
      if (fleet) {
        setSelectedFleet(fleet)
        dispatch(setFleet({ fleetId: fleet.id, currentFleetName: fleet.name }))
      }
    }
  }

  function handleFleetSearchChange(newValue: string) {
    if (!disabled) {
      setFleetSearchString(newValue ?? '')
    }
  }

  useEffect(() => {
    setSelectedFleet(rootFleet)
  }, [rootFleet])

  return (
    <details
      ref={detailsRef}
      className={`${theme} ${cx(theme, { disabled: disabled })} pointer`}
    >
      <summary {...(disabled && { tabIndex: -1 })}>
        <SelectedFleet fleet={selectedFleet}></SelectedFleet>
        <div className={cx('dropdown-state-wrapper')}>
          <KeyboardArrowDownOutlinedIcon />
        </div>
      </summary>
      <div>
        <div className={`${theme} ${cx('input-icon-wrapper', theme)}`}>
          <TextField
            sx={{
              width: '100%',
              '& .MuiInputBase-inputMultiline': { padding: '0 !important' },
              '& .MuiInputBase-input.MuiOutlinedInput-input': { padding: '0.5rem 1rem' }
            }}
            type={'text'}
            inputMode={'search'}
            name={'fleet-search'}
            placeholder={'Search fleets...'}
            InputLabelProps={{ style: { display: 'none' }, shrink: false }}
            value={fleetSearchString}
            onChange={x => handleFleetSearchChange(x.target.value)}
            className={`text-field ${theme}`}
            disabled={disabled}
          />
          <SearchIcon />
        </div>
        {fleetTreeFiltered && (
          <div className={cx('fleets-root', theme)}>
            <FleetNavItems
              fleet={fleetTreeFiltered}
              selectedFleetId={selectedFleet?.id ?? ''}
              onClick={handleFleetRadioValueChange}
              searching={fleetSearchString !== ''}
              depthLevel={0}
              disabled={disabled}
            />
          </div>
        )}
        {!fleetTreeFiltered && <div className={cx('no-results')}>No results</div>}
      </div>
    </details>
  )
}
